import React, { useState, useEffect } from 'react';
import { Button } from '../core/Button';
import { useAllUsers } from '../../graphql/hooks/useAdminDashboard';
import { MoreVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  useUpdateUser,
  useCreateUser,
  useDeleteUser,
  useUserTypes,
  useSendPushNotification,
} from '../../graphql/hooks/useAdminDashboard';
import { ConfirmModal, Modal } from '../core';
import { InputField } from '../core/InputField';
import { CheckboxField } from '../core/CheckboxField';
import { UserProfileContent } from '../UserProfileContent';
import toast from 'react-hot-toast';
import { WorkScheduleModal } from './WorkScheduleModal';
import type { UserWorkSchedule } from '../../graphql/types/user';
import { useSendNewUserEmail } from '../../graphql/hooks/useAdminDashboard';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  vacationDays: number;
  hourlyRate: number;
  entryDate: string;
  status: 'active' | 'inactive';
}

export function UserManagementView() {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersItemsPerPage, setUsersItemsPerPage] = useState(10);
  const [openUserDropdownId, setOpenUserDropdownId] = useState<string | null>(null);
  const [userToEdit, setUserToEdit] = useState<any | null>(null);
  const [userToDelete, setUserToDelete] = useState<any | null>(null);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [showExitCalendar, setShowExitCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [userProfileOpen, setUserProfileOpen] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState<any | null>(null);
  const [floatErrors, setFloatErrors] = useState<Record<string, string>>({});
  const [scheduleMode, setScheduleMode] = useState<'fixed' | 'schedule'>('fixed');
  const [workSchedule, setWorkSchedule] = useState<UserWorkSchedule | null>(null);
  const [workScheduleModalOpen, setWorkScheduleModalOpen] = useState(false);

  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushTargetUser, setPushTargetUser] = useState<any | null>(null);
  const [pushForm, setPushForm] = useState({ title: '', body: '' });
  const [pushError, setPushError] = useState<string | null>(null);

  const { mutate: updateUserMutation } = useUpdateUser();
  const { mutate: createUserMutation } = useCreateUser();
  const { mutate: deleteUserMutation } = useDeleteUser();
  const { mutate: sendPushNotification } = useSendPushNotification();
  const { mutate: deleteCategory } = useSendNewUserEmail();

  const toFloat = (v: string) => v.replace(',', '.');
  const isValidFloat = (v: string) => /^-?[0-9]*[.,]?[0-9]*$/.test(v);
  const isValidInt = (v: string) => /^[0-9]*$/.test(v);

  const handleFloatChange = (field: string, v: string, setter: (v: string) => void) => {
    if (v === '' || isValidFloat(v)) {
      setFloatErrors((prev) => ({ ...prev, [field]: '' }));
      setter(toFloat(v));
    } else {
      setFloatErrors((prev) => ({ ...prev, [field]: 'Nur Zahlen erlaubt' }));
    }
  };

  const { data: allUsers } = useAllUsers(
    usersCurrentPage,
    usersItemsPerPage,
    showInactive,
    debouncedSearch
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  const handleViewUser = (user: any) => {
    setSelectedUserProfile(user);
    setUserProfileOpen(true);
    setOpenUserDropdownId(null);
  };

  const handleOpenPushModal = (user: any) => {
    setPushTargetUser(user);
    setPushForm({ title: '', body: '' });
    setPushError(null);
    setPushModalOpen(true);
    setOpenUserDropdownId(null);
  };

  const handleSendPush = () => {
    if (!pushForm.title.trim() || !pushForm.body.trim()) {
      setPushError('Titel und Nachricht sind erforderlich.');
      return;
    }

    sendPushNotification(
      {
        user_id: pushTargetUser.id,
        title: pushForm.title,
        body: pushForm.body,
      },
      {
        onSuccess: () => {
          setPushModalOpen(false);
          setPushTargetUser(null);
          setPushForm({ title: '', body: '' });
          setPushError(null);
        },
        onError: (error: any) => {
          const gqlError =
            error?.response?.errors?.[0]?.extensions?.debugMessage ||
            error?.response?.errors?.[0]?.message ||
            'Nachricht konnte nicht gesendet werden';
          setPushError(gqlError);
        },
      }
    );
  };

  const { data: userTypes } = useUserTypes();

  const usersPaginator = allUsers?.allUsers.paginatorInfo;

  const initialUserForm = {
    firstName: '',
    lastName: '',
    email: '',
    userType: '',
    vacationDays: '30',
    restVacationDays: '',
    timeBalance: '',
    entryDate: '',
    exitDate: '',
    status: '',
    hourlyWage: '100',
    profession: '',
    internalNumber: '',
    fixedHourDeduction: '0',
    dailyWorkHours: '',
    hasCar: false,
    hasExpenses: false,
    isActive: true,
    hasDatevEnabled: false,
    hasFixedSalary: false,
    hasEmergencyService: false,
  };

  const [newUserForm, setNewUserForm] = useState(initialUserForm);

  const resetForm = () => {
    setNewUserForm(initialUserForm);
    setFormErrors({});
    setFloatErrors({});
    setUserToEdit(null);
    setMutationError(null);
    setScheduleMode('fixed');
    setWorkSchedule(null);
  };

  const validateCreateUser = () => {
    const errors: Record<string, string> = {};

    if (!newUserForm.firstName.trim()) errors.firstName = 'Vorname ist erforderlich';
    if (!newUserForm.lastName.trim()) errors.lastName = 'Nachname ist erforderlich';
    if (!newUserForm.email.trim()) errors.email = 'E-Mail ist erforderlich';
    if (!newUserForm.userType) errors.userType = 'User-Type ist erforderlich';
    if (!newUserForm.entryDate) errors.entryDate = 'Eintrittsdatum ist erforderlich';
    if (!newUserForm.profession.trim()) errors.profession = 'Beruf ist erforderlich';
    if (!newUserForm.status.trim()) errors.status = 'Status ist erforderlich';
    if (!newUserForm.internalNumber.trim()) errors.internalNumber = 'Personalnummer (Datev) ist erforderlich';
    if (!newUserForm.fixedHourDeduction.trim()) errors.fixedHourDeduction = 'Fester Stundenabzug ist erforderlich';
    if (scheduleMode === 'fixed' && !newUserForm.dailyWorkHours.trim()) {
      errors.dailyWorkHours = 'Tägliche Arbeitszeit ist erforderlich';
    }
    if (scheduleMode === 'schedule' && !workSchedule) {
      errors.workSchedule = 'Bitte Arbeitsplan ausfüllen';
    }
    if (!newUserForm.hourlyWage.trim()) errors.hourlyWage = 'Stundenlohn ist erforderlich';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const allUsersData = allUsers?.allUsers.data ?? [];

  const toastSuccess = (message: string) =>
    toast.success(message, {
      duration: 3000,
      style: {
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        color: '#15803d', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#16a34a', secondary: '#f0fdf4' },
    });

  const toastError = (message: string) =>
    toast.error(message, {
      duration: 4000,
      style: {
        background: '#fef2f2', border: '1px solid #fecaca',
        color: '#dc2626', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#dc2626', secondary: '#fef2f2' },
    });

  const handleEditUser = (user: any) => {
    setUserToEdit(user);
    setNewUserForm({
      firstName: user.first_name ?? '',
      lastName: user.last_name ?? '',
      email: user.email ?? '',
      userType: String(user.user_type?.id ?? ''),
      vacationDays: user.holiday_total ?? '',
      restVacationDays: user.open_holidays ?? '',
      timeBalance: user.time_credit ?? '',
      entryDate: user.entry_date ?? '',
      exitDate: user.exit_date ?? '',
      status: user.status ?? '',
      hourlyWage: user.hourly_wage ?? '',
      profession: user.job_title ?? '',
      internalNumber: String(user.internal_number ?? ''),
      fixedHourDeduction: user.fixed_time ?? '',
      dailyWorkHours: user.daily_work_time ?? '',
      hasCar: user.has_car ?? false,
      hasExpenses: user.expenses ?? false,
      isActive: user.active ?? true,
      hasDatevEnabled: user.datev_enabled ?? false,
      hasFixedSalary: user.has_fixed_salary ?? false,
      hasEmergencyService: user.emergency_service_enabled ?? false,
    });
    setScheduleMode(user.work_schedule ? 'schedule' : 'fixed');
    setWorkSchedule(user.work_schedule ?? null);
    setIsAddUserModalOpen(true);
    setOpenUserDropdownId(null);
  };

  const handleSaveUser = () => {
    if (newUserForm.hasDatevEnabled && (!newUserForm.internalNumber.trim() || Number(newUserForm.internalNumber) === 0)) {
      const msg = 'Personalnummer (Datev) muss angegeben werden, wenn Lohnbuchhaltung berücksichtigt werden soll.';
      setMutationError(msg);
      setFloatErrors((prev) => ({ ...prev, internalNumber: msg }));
      return;
    }
    if (userToEdit) {
      updateUserMutation(
        {
          id: userToEdit.id,
          full_name: `${newUserForm.firstName} ${newUserForm.lastName}`,
          email: newUserForm.email || undefined,
          user_type: Number(newUserForm.userType) || undefined,
          job_title: newUserForm.profession || undefined,
          status: newUserForm.status || undefined,
          exit_date: newUserForm.exitDate || undefined,
          active: newUserForm.isActive,
          holiday_total: newUserForm.vacationDays !== '' ? Number(newUserForm.vacationDays) : 0,
          holiday_rest: newUserForm.restVacationDays !== '' ? Number(newUserForm.restVacationDays) : 0,
          time_credit: newUserForm.timeBalance !== '' ? Number(newUserForm.timeBalance) : 0,
          hourly_wage: Number(newUserForm.hourlyWage) || 0,
          fixed_hour_deduction: newUserForm.fixedHourDeduction !== '' ? Number(newUserForm.fixedHourDeduction) : 0,
          internal_number: newUserForm.internalNumber !== '' ? Number(newUserForm.internalNumber) : 0,
          expenses: newUserForm.hasExpenses,
          datev_enabled: newUserForm.hasDatevEnabled,
          has_fixed_salary: newUserForm.hasFixedSalary,
          emergency_service_enabled: newUserForm.hasEmergencyService,
          daily_work_time: scheduleMode === 'fixed'
            ? Number(newUserForm.dailyWorkHours) || 0
            : 0,
          work_schedule: scheduleMode === 'schedule'
            ? workSchedule ?? undefined
            : undefined,
        },
        {
          onSuccess: () => {
            setIsAddUserModalOpen(false);
            resetForm();
            toastSuccess('Benutzer erfolgreich aktualisiert');
          },
          onError: (error: any) => {
            const debugMessage =
              error?.response?.errors?.[0]?.extensions?.debugMessage || '';

            const gqlError = debugMessage.includes('Cannot change work model while future overtime or holidays exist')
              ? 'Das Arbeitszeitmodell kann nicht geändert werden, solange zukünftige Überstunden oder Urlaubsanträge existieren.'
              : debugMessage.includes('Internal number already exists')
              ? 'Diese interne Nummer wird bereits verwendet.'
              : debugMessage.includes('Internal number needs to be provided if Datev is enabled')
              ? 'Personalnummer (Datev) muss angegeben werden, wenn Lohnbuchhaltung berücksichtigt werden soll.'
              : 'Benutzer konnte nicht aktualisiert werden';

            toastError(gqlError);
            setMutationError(gqlError);
          },
        }
      );
    } else {
      const isValid = validateCreateUser();
      if (!isValid) {
        setMutationError('Bitte alle Pflichtfelder ausfüllen.');
        return;
      }
      setMutationError(null);

      createUserMutation(
        {
          first_name: newUserForm.firstName,
          last_name: newUserForm.lastName,
          email: newUserForm.email,
          user_type: Number(newUserForm.userType),
          holidays: parseInt(newUserForm.vacationDays || '0', 10),
          hourly_wage: Number(newUserForm.hourlyWage || 0),
          entry_date: newUserForm.entryDate,
          job_title: newUserForm.profession,
          status: newUserForm.status || 'Aktiv',
          has_car: newUserForm.hasCar,
          expenses: newUserForm.hasExpenses,
          internal_number: Number(newUserForm.internalNumber || 0),
          fixed_hour_deduction: Number(newUserForm.fixedHourDeduction || 0),
          datev_enabled: newUserForm.hasDatevEnabled,
          has_fixed_salary: newUserForm.hasFixedSalary,
          emergency_service_enabled: newUserForm.hasEmergencyService,
          daily_work_time: scheduleMode === 'fixed'
            ? Number(newUserForm.dailyWorkHours) || 0
            : 0,
          work_schedule: scheduleMode === 'schedule'
            ? workSchedule ?? undefined
            : undefined,
        },
        {
          onSuccess: () => {
            setIsAddUserModalOpen(false);
            resetForm();
            toastSuccess('Benutzer erfolgreich erstellt');
          },
          onError: (error: any) => {
            const rawMessage =
              error?.response?.errors?.[0]?.message || '';

            const gqlError = rawMessage.includes('Int cannot represent non 32-bit signed integer') && rawMessage.includes('internal_number')
              ? 'Personalnummer (Datev) ist nicht im richtigen Format.'
              : rawMessage.includes('Internal number needs to be provided if Datev is enabled')
              ? 'Personalnummer (Datev) muss angegeben werden, wenn Lohnbuchhaltung berücksichtigt werden soll.'
              : 'Benutzer konnte nicht erstellt werden';

            toastError(gqlError);
            setMutationError(gqlError);
          },
        }
      );
    }
  };

  const handleDeleteUserClick = (user: any) => {
    setUserToDelete(user);
    setDeleteUserModalOpen(true);
    setOpenUserDropdownId(null);
  };

  const isEditMode = !!userToEdit;

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const formatDateForApi = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleExitDateClick = (date: Date) => {
    setNewUserForm((prev) => ({
      ...prev,
      exitDate: formatDateForApi(date),
    }));
    setShowExitCalendar(false);
  };

  const renderExitCalendar = () => {
    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    ).getDate();

    const firstDay =
      (new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() + 6) % 7;

    const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

    return (
      <>
        {/* Header */}
        <div className="flex justify-between mb-4">
          <button onClick={previousMonth}>
            <ChevronLeft />
          </button>
          <div className="font-medium">
            {currentMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </div>
          <button onClick={nextMonth}>
            <ChevronRight />
          </button>
        </div>

        {/* Weekdays */}
        <div className="grid grid-cols-7 text-xs text-center mb-2">
          {dayNames.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={i} />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const date = new Date(
              currentMonth.getFullYear(),
              currentMonth.getMonth(),
              i + 1
            );

            return (
              <button
                key={i}
                onClick={() => handleExitDateClick(date)}
                className="aspect-square rounded-[8px] text-[14px] hover:bg-[#f5f5f5]"
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Controls Row */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px]">
            <span>Zeige</span>
            <select
              value={usersItemsPerPage}
              onChange={(e) => {
                setUsersItemsPerPage(Number(e.target.value));
                setUsersCurrentPage(1);
              }}
              className="form-select px-2 py-1 border border-[#e0e0e0] rounded text-[#1c1c1c]"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>Einträge</span>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Benutzer suchen..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setUsersCurrentPage(1);
              }}
              className="h-[40px] px-3 border border-[#e0e0e0] rounded-lg text-[14px]"
            />

            <label className="flex items-center gap-2 text-[14px] text-[#6b6b6b]">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => {
                  setShowInactive(e.target.checked);
                  setUsersCurrentPage(1);
                }}
              />
              Inaktive anzeigen
            </label>
          </div>
        </div>

        {/* Add User Button */}
        <Button
          variant="primary"
          onClick={() => setIsAddUserModalOpen(true)}
          className="flex items-center gap-2"
        >
          Neuen Benutzer anlegen
        </Button>
      </div>

      {/* Data Display */}
      {allUsersData?.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-[#6b6b6b] text-[16px]">Keine Benutzer vorhanden</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Table Header */}
          <div className="grid grid-cols-4 gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
            <div>Personalnummer (Datev)</div>
            <div>Name</div>
            <div>Status</div>
            <div>Typ</div>
          </div>

          {/* Table Rows */}
          {allUsersData?.map((entry) => (
            <div
              key={entry.id}
              className="grid grid-cols-4 gap-4 px-4 py-3 text-[16px] items-center hover:bg-[#f5f5f5] transition-colors"
            >
              <div className="text-[#6b6b6b]">
                {entry.internal_number && Number(entry.internal_number) !== 0
                  ? entry.internal_number
                  : '-'}
              </div>
              <div className="text-[#0084c7]">
                {entry.first_name} {entry.last_name}
              </div>
              <div className="text-[#6b6b6b]">{entry.status}</div>
              <div className="text-[#6b6b6b] flex justify-between items-center relative">
                <span>{entry.user_type?.type}</span>

                <button
                  onClick={() =>
                    setOpenUserDropdownId(
                      openUserDropdownId === entry.id ? null : entry.id
                    )
                  }
                  className="p-1 hover:bg-[#f5f5f5] rounded-[6px]"
                >
                  <MoreVertical className="size-5 text-[#6b6b6b]" />
                </button>

                {openUserDropdownId === entry.id && (
                  <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[200px]">
                    <button
                      onClick={() => handleViewUser(entry)}
                      className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                    >
                      Profil ansehen
                    </button>
                    <button
                      onClick={() => {
                        deleteCategory(entry.id, {
                          onSuccess: () => {
                            toastSuccess('Email wurde gesendet');
                          },
                        });
                        setOpenUserDropdownId(null);
                      }}
                      className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                    >
                      Einladungsmail versenden
                    </button>
                    {entry.hasToken && (
                      <button
                        onClick={() => handleOpenPushModal(entry)}
                        className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                      >
                        Push Nachricht senden
                      </button>
                    )}
                    <button
                      onClick={() => handleEditUser(entry)}
                      className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleDeleteUserClick(entry)}
                      className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                    >
                      Löschen
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t border-[#e0e0e0]">
            {/* Info */}
            <div className="text-[#6b6b6b] text-[14px]">
              Zeige{' '}
              {Math.min(
                ((usersPaginator?.currentPage ?? 1) - 1) * usersItemsPerPage + 1,
                usersPaginator?.total ?? 0
              )}{' '}
              bis{' '}
              {Math.min(
                (usersPaginator?.currentPage ?? 1) * usersItemsPerPage,
                usersPaginator?.total ?? 0
              )}{' '}
              von {usersPaginator?.total ?? 0} Einträgen
            </div>

            {/* Page Controls */}
            <div className="flex items-center gap-2">
              {/* Previous */}
              <button
                onClick={() => setUsersCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={usersPaginator?.currentPage === 1}
                className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
              >
                Vorherige
              </button>

              {/* Page Numbers */}
              {(() => {
                const totalPages = usersPaginator?.lastPage ?? 1;
                const pages = [];
                const maxVisiblePages = 5;

                let startPage = Math.max(
                  1,
                  (usersPaginator?.currentPage ?? 1) - Math.floor(maxVisiblePages / 2)
                );
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                if (endPage - startPage < maxVisiblePages - 1) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }

                return pages.map((page) => (
                  <button
                    key={page}
                    onClick={() => setUsersCurrentPage(page)}
                    className={`px-3 py-1 text-[14px] rounded ${
                      usersPaginator?.currentPage === page
                        ? 'bg-[#0084c7] text-white'
                        : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                    }`}
                  >
                    {page}
                  </button>
                ));
              })()}

              {/* Next */}
              <button
                onClick={() =>
                  setUsersCurrentPage((prev) =>
                    Math.min(usersPaginator?.lastPage ?? prev, prev + 1)
                  )
                }
                disabled={usersPaginator?.currentPage === usersPaginator?.lastPage}
                className="px-3 py-1 text-[14px] text-[#6b6b6b] hover:text-[#0084c7] disabled:opacity-50"
              >
                Nächste
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit User Modal */}
      <Modal
        isOpen={isAddUserModalOpen}
        onClose={() => {
          setIsAddUserModalOpen(false);
          resetForm();
          setMutationError(null);
        }}
        title={isEditMode ? 'Benutzer bearbeiten' : 'Benutzer erstellen'}
      >
        <div className="space-y-6">
          {mutationError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-[14px]">
              {mutationError}
            </div>
          )}

          {/* Row 1: Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[#6b6b6b] text-[14px]">Vorname</label>
              <input
                type="text"
                value={newUserForm.firstName}
                onChange={(e) =>
                  setNewUserForm((prev) => ({ ...prev, firstName: e.target.value }))
                }
                className="w-full h-[42px] text-[14px] box-border px-4 py-2 border border-[#e0e0e0] rounded-lg"
              />
              {formErrors.firstName && (
                <div className="text-red-500 text-[12px] mt-1">{formErrors.firstName}</div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-[#6b6b6b] text-[14px]">Nachname</label>
              <input
                type="text"
                value={newUserForm.lastName}
                onChange={(e) =>
                  setNewUserForm((prev) => ({ ...prev, lastName: e.target.value }))
                }
                className="w-full h-[42px] text-[14px] box-border px-4 py-2 border border-[#e0e0e0] rounded-lg"
              />
              {formErrors.lastName && (
                <div className="text-red-500 text-[12px] mt-1">{formErrors.lastName}</div>
              )}
            </div>
          </div>

          {/* Row 2: Email & User Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[#6b6b6b] text-[14px]">E-Mail</label>
              <input
                type="email"
                value={newUserForm.email}
                onChange={(e) =>
                  setNewUserForm((prev) => ({ ...prev, email: e.target.value }))
                }
                className="w-full h-[42px] text-[14px] box-border px-4 py-2 border border-[#e0e0e0] rounded-lg"
              />
              {formErrors.email && (
                <div className="text-red-500 text-[12px] mt-1">{formErrors.email}</div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-[#6b6b6b] text-[14px]">User-Type</label>
              <select
                value={newUserForm.userType}
                onChange={(e) =>
                  setNewUserForm((prev) => ({ ...prev, userType: e.target.value }))
                }
                className="w-full form-select h-[42px] box-border px-4 py-2 border border-[#e0e0e0] rounded-lg"
              >
                <option value="">Bitte auswählen</option>
                {userTypes?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.type}
                  </option>
                ))}
              </select>
              {formErrors.userType && (
                <div className="text-red-500 text-[12px] mt-1">{formErrors.userType}</div>
              )}
            </div>
          </div>

          {/* Active checkbox — edit mode only */}
          {isEditMode && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newUserForm.isActive}
                onChange={(e) =>
                  setNewUserForm({ ...newUserForm, isActive: e.target.checked })
                }
              />
              <span className="text-[#6b6b6b] text-[14px]">Aktiv</span>
            </div>
          )}

          {/* Create mode fields */}
          {!isEditMode && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Urlaubstage"
                  value={newUserForm.vacationDays}
                  onChange={(v) => setNewUserForm((prev) => ({ ...prev, vacationDays: v }))}
                />
                <InputField
                  label="Eintrittsdatum"
                  type="date"
                  value={newUserForm.entryDate}
                  onChange={(v) => setNewUserForm((prev) => ({ ...prev, entryDate: v }))}
                />
                {formErrors.entryDate && (
                  <div className="text-red-500 text-[12px] mt-1">{formErrors.entryDate}</div>
                )}
              </div>

              <div>
                <InputField
                  label="Personalnummer (Datev)"
                  value={newUserForm.internalNumber}
                  onChange={(v) => {
                    if (v === '' || isValidInt(v)) {
                      setFloatErrors((prev) => ({ ...prev, internalNumber: '' }));
                      setNewUserForm((prev) => ({ ...prev, internalNumber: v }));
                    } else {
                      setFloatErrors((prev) => ({ ...prev, internalNumber: 'Nur Zahlen erlaubt' }));
                    }
                  }}
                />
                {formErrors.internalNumber && (
                  <div className="text-red-500 text-[12px] mt-1">{formErrors.internalNumber}</div>
                )}
                {floatErrors.internalNumber && (
                  <div className="text-red-500 text-[12px] mt-1">{floatErrors.internalNumber}</div>
                )}
              </div>
            </>
          )}

          {/* Edit mode fields */}
          {isEditMode && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputField
                    label="Urlaubstage"
                    value={newUserForm.vacationDays}
                    onChange={(v) => {
                      if (v === '' || isValidInt(v)) {
                        setFloatErrors((prev) => ({ ...prev, vacationDays: '' }));
                        setNewUserForm((prev) => ({ ...prev, vacationDays: v }));
                      } else {
                        setFloatErrors((prev) => ({ ...prev, vacationDays: 'Nur ganze Zahlen erlaubt' }));
                      }
                    }}
                  />
                  {floatErrors.vacationDays && (
                    <div className="text-red-500 text-[12px] mt-1">{floatErrors.vacationDays}</div>
                  )}
                </div>
                <div>
                  <InputField
                    label="Rest-Urlaubstage"
                    value={newUserForm.restVacationDays}
                    onChange={(v) => {
                      if (v === '' || /^-?[0-9]*$/.test(v)) {
                        setFloatErrors((prev) => ({ ...prev, restVacationDays: '' }));
                        setNewUserForm((prev) => ({ ...prev, restVacationDays: v }));
                      } else {
                        setFloatErrors((prev) => ({ ...prev, restVacationDays: 'Nur ganze Zahlen erlaubt' }));
                      }
                    }}
                  />
                  {floatErrors.restVacationDays && (
                    <div className="text-red-500 text-[12px] mt-1">{floatErrors.restVacationDays}</div>
                  )}
                </div>
              </div>

              <div>
                <InputField
                  label="Personalnummer (Datev)"
                  value={newUserForm.internalNumber}
                  onChange={(v) => {
                    if (v === '' || isValidInt(v)) {
                      setFloatErrors((prev) => ({ ...prev, internalNumber: '' }));
                      setNewUserForm((prev) => ({ ...prev, internalNumber: v }));
                    } else {
                      setFloatErrors((prev) => ({ ...prev, internalNumber: 'Nur Zahlen erlaubt' }));
                    }
                  }}
                />
                {floatErrors.internalNumber && (
                  <div className="text-red-500 text-[12px] mt-1">{floatErrors.internalNumber}</div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <InputField
                    label="Zeitguthaben"
                    value={newUserForm.timeBalance}
                    onChange={(v) => handleFloatChange('timeBalance', v, (val) =>
                      setNewUserForm((prev) => ({ ...prev, timeBalance: val }))
                    )}
                  />
                  {floatErrors.timeBalance && (
                    <div className="text-red-500 text-[12px] mt-1">{floatErrors.timeBalance}</div>
                  )}
                </div>

                <div className="relative space-y-2">
                  <label className="text-[#6b6b6b] text-[14px]">Austrittsdatum</label>
                  <input
                    type="text"
                    readOnly
                    value={newUserForm.exitDate || 'Datum auswählen'}
                    onClick={() => setShowExitCalendar((prev) => !prev)}
                    className="w-full box-border h-[42px] px-4 border border-[#e0e0e0] rounded-lg cursor-pointer"
                  />
                  {showExitCalendar && (
                    <div className="absolute z-40 mt-2 w-full box-border bg-white border rounded-[12px] shadow-lg p-4">
                      {renderExitCalendar()}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Common fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Beruf"
              value={newUserForm.profession}
              onChange={(v) => setNewUserForm((prev) => ({ ...prev, profession: v }))}
            />
            {formErrors.profession && (
              <div className="text-red-500 text-[12px] mt-1">{formErrors.profession}</div>
            )}

            <InputField
              label="Status"
              value={newUserForm.status}
              onChange={(v) => setNewUserForm((prev) => ({ ...prev, status: v }))}
            />
            {formErrors.status && (
              <div className="text-red-500 text-[12px] mt-1">{formErrors.status}</div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scheduleMode === 'fixed' && (
              <div>
                <InputField
                  label="Daily working hours"
                  value={newUserForm.dailyWorkHours}
                  onChange={(v) =>
                    handleFloatChange('dailyWorkHours', v, (val) =>
                      setNewUserForm((prev) => ({ ...prev, dailyWorkHours: val }))
                    )
                  }
                />
                {(formErrors.dailyWorkHours || floatErrors.dailyWorkHours) && (
                  <div className="text-red-500 text-[12px] mt-1">
                    {formErrors.dailyWorkHours || floatErrors.dailyWorkHours}
                  </div>
                )}
              </div>
            )}

            {scheduleMode === 'schedule' && (
              <div>
                <label className="text-[#6b6b6b] text-[14px]">Zeitmodel</label>
                <button
                  type="button"
                  onClick={() => setWorkScheduleModalOpen(true)}
                  className={`w-full h-[42px] text-[14px] box-border px-4 py-2 border border-[#e0e0e0] rounded-lg text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors ${
                    formErrors.workSchedule ? 'border-red-400' : 'border-[#e0e0e0]'
                  }`}
                >
                  {workSchedule ? 'Bearbeiten' : 'Zeitplan einrichten'}
                  {workSchedule && (
                    <span className="text-[12px] text-[#0084c7] ml-2">
                      ({Object.values(workSchedule)
                        .reduce((a, b) => a + b, 0)
                        .toLocaleString('de-DE', { maximumFractionDigits: 2 })} Std/Woche)
                    </span>
                  )}
                </button>
                {formErrors.workSchedule && (
                  <div className="text-red-500 text-[12px] mt-1">{formErrors.workSchedule}</div>
                )}
              </div>
            )}
            <div>
              <InputField
                label="Stundenlohn"
                value={newUserForm.hourlyWage}
                onChange={(v) => handleFloatChange('hourlyWage', v, (val) =>
                  setNewUserForm((prev) => ({ ...prev, hourlyWage: val }))
                )}
              />
              {formErrors.hourlyWage && (
                <div className="text-red-500 text-[12px] mt-1">{formErrors.hourlyWage}</div>
              )}
              {floatErrors.hourlyWage && (
                <div className="text-red-500 text-[12px] mt-1">{floatErrors.hourlyWage}</div>
              )}
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="scheduleMode"
                checked={scheduleMode === 'schedule'}
                onChange={(e) => {
                  setScheduleMode(e.target.checked ? 'schedule' : 'fixed');
                  setFormErrors((prev) => ({ ...prev, dailyWorkHours: '', workSchedule: '' }));
                }}
              />
              <label htmlFor="scheduleMode" className="text-[14px] text-[#6b6b6b] cursor-pointer">
                Individuellen Arbeitsplan verwenden
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <InputField
                label="Fester Stundenabzug"
                value={newUserForm.fixedHourDeduction}
                onChange={(v) => handleFloatChange('fixedHourDeduction', v, (val) =>
                  setNewUserForm((prev) => ({ ...prev, fixedHourDeduction: val }))
                )}
              />
              {(formErrors.fixedHourDeduction || floatErrors.fixedHourDeduction) && (
                <div className="text-red-500 text-[12px] mt-1">
                  {formErrors.fixedHourDeduction || floatErrors.fixedHourDeduction}
                </div>
              )}
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex gap-6">
            <CheckboxField
              label="Spesen"
              checked={newUserForm.hasExpenses}
              onChange={(checked) =>
                setNewUserForm((prev) => ({ ...prev, hasExpenses: checked }))
              }
            />
            <CheckboxField
              label="In Lohnbuchhaltung berücksichtigen"
              checked={newUserForm.hasDatevEnabled}
              onChange={(checked) =>
                setNewUserForm((prev) => ({ ...prev, hasDatevEnabled: checked }))
              }
            />
            <CheckboxField
              label="Festgehalt"
              checked={newUserForm.hasFixedSalary}
              onChange={(checked) =>
                setNewUserForm((prev) => ({ ...prev, hasFixedSalary: checked }))
              }
            />
            <CheckboxField
              label="Notdienst"
              checked={newUserForm.hasEmergencyService}
              onChange={(checked) =>
                setNewUserForm((prev) => ({ ...prev, hasEmergencyService: checked }))
              }
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="secondary"
              onClick={() => {
                setIsAddUserModalOpen(false);
                setUserToEdit(null);
              }}
            >
              Schließen
            </Button>
            <Button variant="primary" onClick={handleSaveUser}>
              Speichern
            </Button>
          </div>
        </div>
      </Modal>

      {/* Push Notification Modal */}
      <Modal
        isOpen={pushModalOpen}
        onClose={() => {
          setPushModalOpen(false);
          setPushTargetUser(null);
          setPushError(null);
        }}
        title="Push Nachricht senden"
      >
        <div className="space-y-4">
          {pushError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-[14px]">
              {pushError}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[#6b6b6b] text-[14px]">Titel</label>
            <input
              type="text"
              placeholder="Titel der Nachricht"
              value={pushForm.title}
              onChange={(e) => setPushForm((prev) => ({ ...prev, title: e.target.value }))}
              className="w-full h-[42px] text-[14px] box-border px-4 py-2 border border-[#e0e0e0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[#6b6b6b] text-[14px]">Nachricht</label>
            <textarea
              placeholder="Inhalt der Nachricht"
              value={pushForm.body}
              onChange={(e) => setPushForm((prev) => ({ ...prev, body: e.target.value }))}
              rows={4}
              className="w-full text-[14px] box-border px-4 py-2 border border-[#e0e0e0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="secondary"
              onClick={() => {
                setPushModalOpen(false);
                setPushTargetUser(null);
                setPushError(null);
              }}
            >
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleSendPush}>
              Senden
            </Button>
          </div>
        </div>
      </Modal>

      <WorkScheduleModal
        isOpen={workScheduleModalOpen}
        initialSchedule={workSchedule}
        onClose={() => setWorkScheduleModalOpen(false)}
        onSave={(schedule) => {
          setWorkSchedule(schedule);
          setWorkScheduleModalOpen(false);
          setFormErrors((prev) => ({ ...prev, workSchedule: '' }));
        }}
      />

      {/* Delete User Modal */}
      <ConfirmModal
        isOpen={deleteUserModalOpen}
        onClose={() => setDeleteUserModalOpen(false)}
        title="Benutzer löschen"
        message={
          userToDelete
            ? `Möchten Sie den Benutzer "${userToDelete.first_name} ${userToDelete.last_name}" wirklich löschen?`
            : ''
        }
        onConfirm={() => {
          if (userToDelete) {
            deleteUserMutation(
              { id: userToDelete.id },
              {
                onSuccess: () => {
                  setDeleteUserModalOpen(false);
                  setUserToDelete(null);
                },
              }
            );
          }
        }}
      />

      {/* User Profile Modal */}
      <Modal
        isOpen={userProfileOpen}
        onClose={() => setUserProfileOpen(false)}
        title="Benutzerprofil"
        size="full"
      >
        {selectedUserProfile && (
          <UserProfileContent userData={selectedUserProfile} />
        )}
      </Modal>
    </div>
  );
}